import { getIsString } from 'AppSrc/core/utility-functions/string-fun';
import {
  VF_TEXTAREA,
} from 'AppSrc/core/components/forms/form-base-components/v-f-textarea';
import {
  VF_BUTTON_SELECTOR,
} from 'AppSrc/core/components/forms/form-base-components/v-f-button-selector';
import {
  VF_DROPDOWN,
} from 'AppSrc/core/components/forms/form-base-components/v-f-dropdown';
import {
  VF_INPUT,
} from 'AppSrc/core/components/forms/form-base-components/v-f-input';
import {
  VF_MODAL,
} from 'AppSrc/core/components/forms/form-base-components/v-f-modal';
import {
  VF_DROPZONE,
} from 'AppSrc/core/components/forms/form-base-components/v-f-dropzone';
import {
  VF_DATE_PICKER,
} from 'AppSrc/core/components/forms/form-base-components/v-f-date-picker';
import {
  VF_MULTI_SELECT,
} from 'AppSrc/core/components/forms/form-base-components/v-f-multi-select';
import { APPROVER_GROUP_NAMES } from 'AppSrc/core/components/forms/form-types/create-crq/approvers-section/approvers-section-constants';
import { dateToDefaultTz } from 'AppSrc/core/utility-functions/date-functions';

// functions for formatting api crq properties into form values

// convert incoming values to the correct format for form values
const convertToValueOrNull = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  // if incoming value is a boolean, return as is
  if (typeof value === 'boolean') {
    return value;
  }
  // all other types (eg numeric) should be strings
  return String(value);
};

const convertToValueArrayOrNull = (value) => {
  if (value === undefined || value === null) {
    return null;
  }
  // if we get an empty string as a value we treat it as null
  if (getIsString(value)) {
    if (value.length === 0) {
      return null;
    }
  }
  if (Array.isArray(value)) {
    return value.map(convertToValueOrNull);
  }

  return [convertToValueOrNull(value)];
};

const convertToDefaultTimezoneOrNull = (value) => {
  const strValue = convertToValueOrNull(value);
  if (!value) return value;
  const dateInTz = dateToDefaultTz(strValue);
  return dateInTz ? dateInTz.toISOString() : null;
};


const noConversion = (value) => value;

// Method to return the correct name value for an approver
// if the approver is a user there will be a value for name and group
// otherwise if the approver is an approver_group, just the group is filled in
const getApproverName = (approver) => {
  const {
    // group will be the name of the users group for user approvers
    // or the group name for group approvers
    group,
    // name is the name of the user if this is a user.
    // It will be blank for approver groups
    name,
  } = approver;
  if (name) {
    // this is an approver user. The name displayed should be the name
    // plus the group name in brackets if there is one
    const groupSuffix = group ? ` (${group})` : '';
    return `${name}${groupSuffix}`;
  }
  // this is an approver group, so return the group name
  return group;
};

const getApproverstatus = (approver) => {
  const {
    status,
  } = approver;
  if (status) {
    return status;
  }
  return null;
};
const formatApprover = (value) => {
  if (!value) return value;
  return value.map((approver) => {
    const name = getApproverName(approver);
    const status = getApproverstatus(approver);
    return ({
      // no group id is returned from the crq data for group approvers so we leave id as null
      id: null,
      name,
      readOnly: true,
      status,
    });
  });
};

const fieldTypeFormatters = {
  [VF_TEXTAREA]: convertToValueOrNull,
  [VF_BUTTON_SELECTOR]: convertToValueArrayOrNull,
  [VF_DROPDOWN]: convertToValueOrNull,
  [VF_INPUT]: convertToValueOrNull,
  [VF_MODAL]: noConversion, // TODO sort out the modal value
  [VF_DROPZONE]: noConversion, // conversion from wii is done by hand
  [VF_DATE_PICKER]: convertToDefaultTimezoneOrNull,
  [VF_MULTI_SELECT]: convertToValueArrayOrNull,
};

const fieldSpecificFormatters = {
  [APPROVER_GROUP_NAMES]: formatApprover,
};

// Get the formatter for a field based on it's field id and type.
// If there is a specific formatter for a field id, use that
// otherwise use field type. Default to noConversion
const getFormatterForField = ({
  fieldType,
  fieldId,
}) => {
  const fieldSpecificFormatter = fieldSpecificFormatters[fieldId];
  if (fieldSpecificFormatter) {
    return fieldSpecificFormatter;
  }

  const fieldTypeFormatter = fieldTypeFormatters[fieldType];
  if (fieldTypeFormatter) {
    return fieldTypeFormatter;
  }
  // If no formatter found, default to no conversion
  return noConversion;
};

// function that takes a server value from a crq, and the fieldType
// and returns the value converted into the correct format for the formData
const formatApiValueForForm = ({
  value,
  fieldType,
  fieldId,
}) => {
  const formatter = getFormatterForField({
    fieldType,
    fieldId,
  });
  return formatter(value);
};

// blank values expected by the different field types
// any not included in here have a blank value of null
const fieldTypeBlankValues = {
  [VF_TEXTAREA]: '',
  [VF_BUTTON_SELECTOR]: [],
  [VF_INPUT]: '',
};

// get the expected blank value for a fieldType.
// Note that this is different from initialValue, which represents the value
// that should be populated when the form field first appears.
// blank value is the value expected when the field is empty
const getBlankValueForFieldType = (fieldType) => {
  const blankValue = fieldTypeBlankValues[fieldType];
  return blankValue === undefined ? null : blankValue;
};

export {
  formatApiValueForForm,
  getBlankValueForFieldType,
};
